import { Details, Error } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { withTheme } from '@mui/styles';
import { ClientJS } from 'clientjs';
import React, { type ReactNode } from 'react';
import pjson from '../../package.json';
import { log } from '../_tools/AxiosVars';
import BoxMaterial from '../_tools/BoxMaterial';
import { getHistory } from '../_tools/History';
import Meta from '../_tools/Meta';
import { getFirmaName, getIdFirma, getIdUser, getMitarbeiter4Id, getUserName } from '../_tools/Session';
import { formatText } from '../_tools/Texte';
import { checkUndefinedNullOrEmpty } from '../_tools/Tools';
import Iconify from '../components/iconify/component';
import { type RuntimeError } from '../errors/RuntimeError';
import { type UnknownError } from '../errors/UnknownError';
import LandingPage from '../layouts/LandingPage';
import { type CustomTheme } from './ThemeProvider';

const client = new ClientJS();

type CustomErrorType = {
    code?: string;
    message?: string;
    stack?: string;
};

type IState = {
    collapseError?: boolean;
    error?: CustomErrorType;
};

type IProps = {
    children: ReactNode;
    theme: CustomTheme;
};

type CustomError = RuntimeError | UnknownError;

class ErrorHandler extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = { error: undefined, collapseError: false };
        this.handleCollapse = this.handleCollapse.bind(this);
    }

    shouldComponentUpdate() {
        return true;
    }

    componentDidCatch(error: CustomError) {
        const newError = Object.assign(
            {},
            {
                code: error?.code,
                message: error?.message,
                name: error?.name,
                stack: error?.stack,
            }
        );
        if (checkUndefinedNullOrEmpty(newError.code)) {
            if (!checkUndefinedNullOrEmpty(newError.stack)) {
                newError.code = 'runtimeError';
            } else {
                newError.code = 'unknownError';
            }
        }
        this.setState({ error: newError });
    }

    handleCollapse() {
        this.setState({ collapseError: !this.state.collapseError });
    }

    themeSpacing(size: number) {
        return size * 4;
    }

    render() {
        const { children, theme } = this.props;
        const { collapseError, error } = this.state;

        if (!checkUndefinedNullOrEmpty(error)) {
            const errorStack = error?.stack?.split('\n')?.map((i) => i?.trim());
            errorStack?.shift();

            const hist = [];
            getHistory().map((locObj) => {
                hist.push(
                    formatText("{ Pathname: '{0}', Replace: {1}, State: {2} }", locObj?.pathname || 'undefined', locObj?.replace || 'undefined', JSON.stringify(locObj?.state || {}).replace('"', "'"))
                );
            });

            const returnErrStr =
                'Ein Fehler ist aufgetreten!\n' +
                '------------------------------------------------------\n' +
                'Firmendaten\n' +
                '------------------------------------------------------\n' +
                'Firma: ' +
                (getFirmaName() || '<unbekannt>') +
                ' (' +
                (getIdFirma() || '<unbekannt>') +
                ')\n' +
                'Benutzer: ' +
                (getUserName() || '<unbekannt>') +
                ' (' +
                (getIdUser() || '<unbekannt>') +
                ')\n' +
                'Login: ' +
                (getIdFirma() || '<unbekannt>') +
                '.' +
                (getMitarbeiter4Id(getIdUser())?.s || '<unbekannt>') +
                '\n' +
                '------------------------------------------------------\n' +
                'Technische Daten\n' +
                '------------------------------------------------------\n' +
                'Betriebssystem: ' +
                client?.getOS() +
                ' ' +
                client?.getOSVersion() +
                '\n' +
                'Browser: ' +
                client?.getBrowser() +
                ' ' +
                client?.getBrowserVersion() +
                '\n' +
                'Browser-Engine: ' +
                client?.getEngine() +
                ' ' +
                client?.getEngineVersion() +
                '\n' +
                '------------------------------------------------------\n' +
                'AHD Cars Daten\n' +
                '------------------------------------------------------\n' +
                'AHD Cars Version: ' +
                pjson?.version +
                '\n' +
                'Aktuell aufgerufener Pfad: ' +
                location?.href +
                '\n' +
                'Historie:\n' +
                hist?.join('\n->\n') +
                '\n' +
                '------------------------------------------------------\n' +
                'Fehlerdaten\n' +
                '------------------------------------------------------\n' +
                'Code: ' +
                (error?.code ? error?.code : '-') +
                '\n' +
                'Beschreibung: ' +
                (error?.message ? error?.message : '-') +
                '\n' +
                'Stack:\n' +
                errorStack?.join('\n');

            console.error(returnErrStr);

            log(getIdFirma() || 0, getMitarbeiter4Id(getIdUser())?.sub || 0, 2, returnErrStr).then(() => console.log('Fehler wurde gesendet!'));

            return (
                <div
                    style={{
                        width: '100vw',
                        height: '100vh',
                        padding: 8,
                        overflow: 'hidden',
                    }}
                >
                    <Meta title="Oh nein! Es ist ein Fehler aufgetreten" />
                    <LandingPage gray>
                        <BoxMaterial bodyStyle={{ padding: 15 }} style={{ marginTop: 35, marginBottom: 15 }}>
                            <div style={{ textAlign: 'center' }}>
                                <Error style={{ fontSize: 50, fill: theme?.palette?.error?.main }} />
                                <Typography
                                    key="errorCode"
                                    component="p"
                                    style={{ color: theme?.palette?.text?.disabled, fontSize: '0.85em', fontWeight: 300, lineHeight: 1.6, textTransform: 'none' }}
                                >
                                    {checkUndefinedNullOrEmpty(error.code) ? 'unknown' : error.code}
                                </Typography>
                                <Typography key="errorText" variant="h6" sx={{ mb: 1, fontWeight: 700, wordBreak: 'break-all' }}>
                                    {error.message}
                                </Typography>
                                <Collapse in={collapseError}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography key="errorStack" variant="body1" sx={{ textAlign: 'left', maxHeight: 250, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                        {error.stack}
                                    </Typography>
                                </Collapse>
                                <Stack direction="row" gap={2} sx={{ mt: 2 }} justifyContent="center">
                                    <Button variant="contained" onClick={() => this.setState({ collapseError: !collapseError })} startIcon={<Details />}>
                                        Details anzeigen
                                    </Button>
                                    <Button variant="contained" onClick={() => location.reload()} startIcon={<Iconify icon="mdi:refresh" />}>
                                        Neu laden
                                    </Button>
                                </Stack>
                            </div>
                        </BoxMaterial>
                    </LandingPage>
                </div>
            );
        } else {
            return children;
        }
    }
}

export default React.memo(withTheme(ErrorHandler));
